import _ from 'lodash'
import $ from 'jquery'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import ObjectSelectorForm from 'scoreboard/components/object-selector-form'
import FormView from 'scoreboard/components/form-view'
import EditableButton from 'scoreboard/components/editable-button'
import metadata from 'scoreboard/components/metadata'

/**
 * Renders a button that shows the currently selected domain object, and when
 * clicked, opens an ObjectSelector dialog to choose a new one.
 */
const ObjectSelectorButton = EditableButton.extend({
	events: {
		'click': 'onClick'
	},

	/**
	 * @constructs
	 * @extends EditableButton
	 *
	 * @param {Object} options
	 *
	 * @param {Object} [options.domainObject] - the initially selected domain object.
	 * If null, option.message will be displayed as the button text
	 *
	 * @param {String} options.section - the section of the domain object
	 * (ex. 'datasets', 'scorecards')
	 *
	 * @param {String} options.message - text to be used for the button if
	 * no domain object is selected; also used as the title for the object
	 * selector dialog
	 *
	 * @param {Object} [options.objectSelectorOptions] - any extra options to pass
	 * along to the ObjectSelector
	 *
	 * @param {Function} [options.testSelectedObject] - a FormView-compatible
	 * "test" function that receives the selected object; return an error message,
	 * or a nil value to pass validation
	 *
	 * @param {Function} [options.onSelect] - a callback function that is called
	 * when a new object is selected. The dialog won't close until the return
	 * value of the callback is resolved by $.when. This means you can return a
	 * promise from the callback to prevent the dialog from closing until the
	 * promise is resolved. Alternatively, you can return nothing for the dialog
	 * to close immediately. The function takes the selected object as an argument.
	 */
	initialize: function (options) {
		EditableButton.prototype.initialize.apply(this, arguments)
		this.domainObject = options.domainObject
		this.options.label = _.get(this.domainObject, 'label')
	},

	onClick: function () {
		let selectorOptions = _.extend({
			title: i18n.get(this.options.message),
			section: this.options.section,
			primaryButtonKey: 'common.done',
			showProjectSelector: true,
			testSelectedObject: this.options.testSelectedObject
		}, this.options.objectSelectorOptions)

		if (this.domainObject) {
			_.extend(selectorOptions, {
				id: this.domainObject.id,
				orgId: this.domainObject.organizationId,
				projectId: this.domainObject.projectId
			})
		}

		this.objectSelector = new ObjectSelectorForButton(selectorOptions)

		this.objectSelector.on('select', selected => {
			if (this.options.onSelect) {
				$.when(this.options.onSelect(selected)).done(() => {
					this.afterSelect(selected)
				})
			} else {
				this.afterSelect(selected)
			}
		})

		appDisplay.dialog(this.objectSelector)
	},

	afterSelect: function(selected) {
		// close the object selector dialog
		this.objectSelector.trigger('done')
		if (selected.id == _.get(this.domainObject, 'id')) {
			return
		}

		this.domainObject = selected
		// #39878 Normally we'd escape this text, but it already is
		this.renderLabel(this.domainObject.label)
		this.trigger('change', selected)
	},

	val: function (newVal) {
		if (newVal !== undefined) {
			this.domainObject = newVal
		}
		return this.domainObject
	}
})

const ObjectSelectorForButton = ObjectSelectorForm.extend({
	initialize: function (options) {
		this.options = options
		this.FormView = FormView.extend({
			test: this.test.bind(this)
		})
		ObjectSelectorForm.prototype.initialize.call(this, options)
	},

	test: function () {
		let selected = this.objectSelector.selected()
		if (selected) {
			let domainType = metadata[this.options.section]?.domainType
			return _.invoke(this.options, 'testSelectedObject', selected, domainType)
		} else {
			return i18n.get('common.select.required')
		}
	}
})

export default ObjectSelectorButton

ObjectSelectorButton.__filename__ = 'components/object-selector-button.js'
